import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, errorText } from "@/lib/api";
import { Field, TextField, PrimaryButton, GhostButton, inputClass } from "./fields";
import ImageField from "./ImageField";
import GalleryEditor from "./GalleryEditor";
import SpecSheetEditor from "./SpecSheetEditor";
import BrochureField from "./BrochureField";

const TABS = [["card", "Card"], ["detail", "Detail Page"], ["specsheet", "Spec Sheet & Brochure"]];

const EMPTY = (brandSlug) => ({
  brand_slug: brandSlug || "",
  name: "",
  slug: "",
  category: "",
  tagline: "",
  image: "",
  specs: [
    { label: "Length", value: "" },
    { label: "Guests", value: "" },
    { label: "Top Speed", value: "" },
    { label: "Price", value: "On Request" },
  ],
  featured: false,
  order: 0,
  description: "",
  highlights: [],
  gallery: [],
  spec_sheet: [],
  brochure_url: "",
});

export default function YachtForm({ open, onClose, yacht, brands, defaultBrand, onSaved }) {
  const [form, setForm] = useState(yacht ? { ...EMPTY(), ...yacht, specs: yacht.specs.map((s) => ({ ...s })) } : EMPTY(defaultBrand));
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("card");
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const setSpec = (i, k, v) => setForm((f) => ({ ...f, specs: f.specs.map((s, j) => (j === i ? { ...s, [k]: v } : s)) }));
  const addSpec = () => setForm((f) => ({ ...f, specs: [...f.specs, { label: "", value: "" }] }));
  const removeSpec = (i) => setForm((f) => ({ ...f, specs: f.specs.filter((_, j) => j !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...form,
      order: Number(form.order) || 0,
      slug: form.slug || null,
      specs: form.specs.filter((s) => s.label.trim() && s.value.trim()),
      highlights: (form.highlights || []).map((h) => h.trim()).filter(Boolean),
      gallery: (form.gallery || []).map((g) => g.trim()).filter(Boolean),
      spec_sheet: (form.spec_sheet || [])
        .map((g) => ({ group: g.group.trim(), items: g.items.filter((it) => it.label.trim() && it.value.trim()) }))
        .filter((g) => g.group && g.items.length),
    };
    delete payload.id;
    delete payload.brand;
    delete payload.related;
    try {
      if (yacht?.id) await api.put(`/admin/yachts/${yacht.id}`, payload);
      else await api.post("/admin/yachts", payload);
      toast.success(yacht ? "Model updated." : "Model added.");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errorText(err, "Could not save model."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-none border-navy-ink/10 bg-navy sm:max-w-3xl" data-testid="yacht-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-light text-navy-ink">{yacht ? `Edit ${yacht.name}` : "Add a model"}</DialogTitle>
        </DialogHeader>
        <div className="mt-2 flex gap-1 border-b border-navy-ink/10" data-testid="yacht-form-tabs">
          {TABS.map(([key, label]) => (
            <button key={key} type="button" onClick={() => setTab(key)} data-testid={`yacht-form-tab-${key}`}
              className={`-mb-px border-b-2 px-4 py-2.5 text-[10px] uppercase tracking-[0.3em] transition-colors ${tab === key ? "border-gold text-gold" : "border-transparent text-mist hover:text-navy-ink"}`}>
              {label}
            </button>
          ))}
        </div>
        <form onSubmit={submit} className="mt-4 space-y-6" data-testid="yacht-form">
          <div className={tab === "card" ? "space-y-6" : "hidden"}>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Brand">
              <Select value={form.brand_slug} onValueChange={set("brand_slug")}>
                <SelectTrigger className={inputClass} data-testid="yacht-form-brand"><SelectValue placeholder="Select brand" /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {brands.map((b) => <SelectItem key={b.slug} value={b.slug} data-testid={`yacht-form-brand-${b.slug}`}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <TextField label="Model name" required value={form.name} onChange={set("name")} testId="yacht-form-name" placeholder="Manhattan 55" />
            <TextField label="Category" value={form.category} onChange={set("category")} testId="yacht-form-category" placeholder="Flybridge / Sport Yacht / Dayboat" />
            <TextField label="Display order" type="number" value={form.order} onChange={set("order")} testId="yacht-form-order" />
            <TextField label="URL slug" hint="Leave blank to generate from the name." value={form.slug || ""} onChange={set("slug")} testId="yacht-form-slug" placeholder="manhattan-55" />
          </div>
          <TextField label="Tagline" multiline value={form.tagline} onChange={set("tagline")} testId="yacht-form-tagline" placeholder="One elegant sentence about this model." />
          <ImageField label="Card image" value={form.image} onChange={set("image")} testId="yacht-form-image" />

          <Field label="Key specifications (card)">
            <div className="space-y-2">
              {form.specs.map((s, i) => (
                <div key={i} className="flex gap-2" data-testid={`yacht-form-spec-${i}`}>
                  <input value={s.label} onChange={(e) => setSpec(i, "label", e.target.value)} placeholder="Label" className={`h-10 w-2/5 border px-3 text-sm ${inputClass}`} data-testid={`yacht-form-spec-${i}-label`} />
                  <input value={s.value} onChange={(e) => setSpec(i, "value", e.target.value)} placeholder="Value" className={`h-10 flex-1 border px-3 text-sm ${inputClass}`} data-testid={`yacht-form-spec-${i}-value`} />
                  <button type="button" onClick={() => removeSpec(i)} className="px-2 text-mist hover:text-red-700" aria-label="Remove spec" data-testid={`yacht-form-spec-${i}-remove`}><Trash2 size={14} /></button>
                </div>
              ))}
              {form.specs.length < 8 && (
                <GhostButton type="button" onClick={addSpec} data-testid="yacht-form-add-spec" className="inline-flex items-center gap-2"><Plus size={12} /> Add spec</GhostButton>
              )}
            </div>
          </Field>
          </div>

          <div className={tab === "detail" ? "space-y-6" : "hidden"}>
            <TextField label="Description" multiline rows={6} value={form.description} onChange={set("description")} testId="yacht-form-description" placeholder="The story of this model — shown on the detail page and in the brochure." />
            <TextField label="Highlights" hint="One per line." multiline rows={5} value={(form.highlights || []).join("\n")} onChange={(v) => set("highlights")(v.split("\n"))} testId="yacht-form-highlights" placeholder={"Retractable hardtop\nBeach club"} />
            <GalleryEditor value={form.gallery || []} onChange={set("gallery")} />
          </div>

          <div className={tab === "specsheet" ? "space-y-6" : "hidden"}>
            <SpecSheetEditor value={form.spec_sheet || []} onChange={set("spec_sheet")} />
            <BrochureField value={form.brochure_url || ""} onChange={set("brochure_url")} />
          </div>

          <div className="flex items-center justify-between border-t border-navy-ink/10 pt-6">
            <label className="flex items-center gap-3 text-xs text-navy-ink">
              <Switch checked={form.featured} onCheckedChange={set("featured")} data-testid="yacht-form-featured" />
              Feature on homepage
            </label>
            <div className="flex gap-3">
              <GhostButton type="button" onClick={onClose} data-testid="yacht-form-cancel">Cancel</GhostButton>
              <PrimaryButton type="submit" disabled={busy || !form.brand_slug} data-testid="yacht-form-submit">{busy ? "Saving…" : "Save model"}</PrimaryButton>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
